<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { APP_EVENTS, emitAppEvent } from '@/Utils/appEventBus'
import { computed, ref, getCurrentInstance, onMounted, onBeforeUnmount, watch } from 'vue'
import { useNotification } from "@kyvg/vue3-notification"
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import StreamService from '@/services/StreamService'
import 'moment-timezone'
import _ from 'lodash'
import TabGroup from '../../components/Atoms/TabGroup.vue'
import TabItem from '../../components/Atoms/TabItem.vue'
import Tooltip from '../../components/Atoms/Tooltip.vue'
import FormButton from '../../components/Atoms/FormButton.vue'
import InlineDateTimePicker from '../../components/Atoms/InlineDateTimePicker.vue'
import ConfirmModal from '../../components/modals/ConfirmModal.vue'
import IconBase from '../../components/icon/IconBase.vue'
import IconRepeat from '../../components/icon/IconRepeat.vue'
import { track, EVENTS } from '@/lib/tracking'

const instance = getCurrentInstance()
const root = instance.proxy.$root
const store = useStore()
const { notify } = useNotification()

// Props
const props = defineProps({
	stream: {
		type: Object,
		required: true,
		validator(v) {
			return Object.prototype.hasOwnProperty.call(v, '_id')
		}
	},
	videoFiles: {
		type: Array,
		required: false
	},
	videoFilesLoaded: {
		type: Boolean,
		default: true,
	},
	streamAlive: {
		type: Boolean,
		default: false
	}
})

// Emits
const emit = defineEmits({
	'scheduler-settings-updated': () => true,
	'refresh-stream': () => true,
	'stream-updated': () => true,
	'start-stream': () => true,
})

// Refs
const ScheduleModes = ref({
	Loop: 'loop',
	Datetime: 'datetime'
})
const savedSchedulerConfig = ref(null)
const scheduleMode = ref(null)
const scheduledDateTime = ref(null)
const schedulerConfigProcessing = ref(false)
const scheduleConfigLoaded = ref(false)
const infinityLoopFacebookWarningShown = ref(false)

// Computed
const addonSubscriptions = computed(() => store.getters['User/addonSubscriptions'])

const productsFeatures = computed(() => store.state.User.aggregatedDefinition)

const canSaveScheduleSetting = computed(() => {
	if (!savedSchedulerConfig.value) return

	let changed = savedSchedulerConfig.value.mode !== scheduleMode.value
	if (scheduleMode.value === 'loop') return changed

	const hasDatetime = !!scheduledDateTime.value
	if (!hasDatetime) return false

	const scheduledMs = new Date(scheduledDateTime.value).getTime()
	changed = scheduledMs !== savedSchedulerConfig.value.datetime
	return changed
})

const hasEnabledVideo = computed(() => {
	const result = props.videoFiles.find((video => video.enabled === true))
	if (result) {
		return true
	} else return false
})

const infiniteLoopDisabled = computed(() => {
	const scheduleSubscription = addonSubscriptions.value.find(sub => sub.category === 'scheduled')
	if (!scheduleSubscription) return true
	if (schedulerConfigProcessing.value) return true
	if (!scheduleSubscription.package?.definition?.schedulerInfiniteLoop) return true
	return false
})

// Hooks
onMounted(async () => {
	await setupScheduling()
})

// Keep the shared StreamCache in sync with the picker so other components
// (e.g. ScheduleStreamPreviewPane's "Estimated end time") can react live to
// the user's input even before they click Save.
watch([scheduledDateTime, scheduleMode], ([datetime, mode]) => {
	if (!scheduleConfigLoaded.value) return
	const current = store.getters['StreamCache/getSchedulerConfigByStreamId'](props.stream._id) || {}
	const nextConfig = {
		...current,
		mode,
		datetime: datetime ? new Date(datetime).getTime() : null,
	}
	store.commit('StreamCache/SET_SCHEDULER_CONFIG', { streamId: props.stream._id, config: nextConfig })
})

// Methods
const classes = (isActive) => `flex h-8 items-center text-sm rounded capitalize justify-center px-2 w-full hover:bg-surface-4 disabled:hover:bg-surface-3 ${isActive ? 'bg-surface-4' : 'bg-surface-3'}`

const navigateToPayments = () => {
	window.location.href = '/app/subscribe'
}

const setupScheduling = async () => {
	// Use cached store to prevent duplicate calls
	const schedulerConfig = await store.dispatch('StreamCache/getSchedulerConfig', props.stream._id) || {}
	savedSchedulerConfig.value = schedulerConfig
	scheduleMode.value = schedulerConfig.mode

	const { datetime } = schedulerConfig
	if (datetime) {
		scheduledDateTime.value = new Date(datetime).toISOString()
	}

	scheduleConfigLoaded.value = true

	setMinScheduleDateTime()
	emit('scheduler-settings-updated', savedSchedulerConfig.value)
}

const setMinScheduleDateTime = () => {
	return new Date(
		Date.now() + 180 * 1000
	).toISOString()
}

// Quick-schedule presets: one click for the common cases, so users rarely need
// to touch the calendar/time spinner.
const schedulePresets = computed(() => {
	const now = Date.now()
	const tomorrow9 = new Date()
	tomorrow9.setDate(tomorrow9.getDate() + 1)
	tomorrow9.setHours(9, 0, 0, 0)
	return [
		{ label: 'In 15 min', value: new Date(now + 15 * 60 * 1000) },
		{ label: 'In 1 hour', value: new Date(now + 60 * 60 * 1000) },
		{ label: 'In 3 hours', value: new Date(now + 3 * 60 * 60 * 1000) },
		{ label: 'Tomorrow 9 AM', value: tomorrow9 },
	]
})

const applySchedulePreset = (value) => {
	scheduledDateTime.value = value
}
const onScheduleModeChanged = async () => {
	const updatedMode = scheduleMode.value

	if (updatedMode === 'loop') {
		const playlistCanLoop = _.get(productsFeatures.value, 'scheduled.loopingFeature', false)
		if (!playlistCanLoop) {
			showModal('feature-upgrade')
			scheduleMode.value = ScheduleModes.value.Datetime
		}

		// Infinite loop mode does not support Facebook
		const isFacebookContained = props.stream.platforms.some((item) => item.template === 'facebook live' && item.linkedServiceCreds)
		if (isFacebookContained && savedSchedulerConfig.value.mode !== ScheduleModes.Loop && !infinityLoopFacebookWarningShown.value) {
			showModal('infinite-loop-warning')
			infinityLoopFacebookWarningShown.value = true
			scheduleMode.value = ScheduleModes.Datetime
		}
	}
}

const infiniteLoopConfirmed = async () => {
	scheduleMode.value = ScheduleModes.value.Loop
	await saveSchedulerConfig()
}
const saveSchedulerConfig = async () => {
	if (schedulerConfigProcessing.value) return
	if (!props.stream.enabled) {
		showModal('stream-status-warning')
		return
	}

	schedulerConfigProcessing.value = true

	try {
		await StreamService.saveStreamScheduleSettings(
			props.stream._id,
			scheduleMode.value,
			new Date(scheduledDateTime.value)
		)

		track(EVENTS.SCHEDULED_STREAM_SCHEDULE_CONFIGURED, {
			scheduleMode: scheduleMode.value,
			enabledVideoCount: (props.videoFiles || []).filter((video) => video.enabled).length,
		})

		const updatedConfig = { mode: scheduleMode.value }
		if (scheduleMode.value === 'datetime' && hasEnabledVideo.value) {
			updatedConfig.datetime = new Date(
				scheduledDateTime.value
			).getTime()
		} else if (scheduleMode.value === 'datetime' && !hasEnabledVideo.value) {
			showModal('enable-video-warning')
		} else if (scheduleMode.value === 'loop') {
			scheduledDateTime.value = null

			// Toggle off all OAuth Facebook platforms
			await Promise.all(props.stream.platforms.map((item) => {
				if (item.enabled && item.template === 'facebook live' && item.linkedServiceCreds) {
					StreamService.toggleStreamPlatform(props.stream._id, item._id)
				}
			}))
			emit('refresh-stream')
		}

		savedSchedulerConfig.value = updatedConfig
		emit('scheduler-settings-updated', savedSchedulerConfig.value)
		emitAppEvent(APP_EVENTS.SCHEDULE_MODE, scheduleMode.value)
	} catch (e) {
		notify({
			group: 'error',
			text: 'Couldn’t update the scheduler settings.'
		})
		console.log(e)
	}

	schedulerConfigProcessing.value = false

	emit('stream-updated', { platforms: props.stream.platforms })
}

const loopConfirm = () => {
	if (!hasEnabledVideo.value) {
		showModal('enable-video-warning')
	} else {
		showModal('enable-video-confirm')
	}
}

const formatDurationHMS = (totalSecs) => {
	const h = Math.floor(totalSecs / 3600);
	const m = Math.floor((totalSecs % 3600) / 60);
	const s = Math.floor(totalSecs % 60);
	return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

const formatTotalDuration = () => {
	let total = 0;
	if (props.videoFiles) {
		props.videoFiles.forEach(v => {
			if (v.enabled && v.mediaInfo && v.mediaInfo.duration) total += v.mediaInfo.duration;
		});
	}
	return formatDurationHMS(total);
}

const elapsedSeconds = ref(0)
let elapsedTimer = null
let elapsedStartTs = null

const startElapsed = () => {
	if (elapsedTimer) return
	elapsedStartTs = Date.now() - (elapsedSeconds.value * 1000)
	elapsedTimer = setInterval(() => {
		elapsedSeconds.value = Math.floor((Date.now() - elapsedStartTs) / 1000)
	}, 1000)
}

const stopElapsed = (reset = false) => {
	if (elapsedTimer) {
		clearInterval(elapsedTimer)
		elapsedTimer = null
	}
	if (reset) {
		elapsedSeconds.value = 0
		elapsedStartTs = null
	}
}

watch(() => props.streamAlive, (alive) => {
	if (alive) startElapsed()
	else stopElapsed(true)
}, { immediate: true })

onBeforeUnmount(() => stopElapsed())

const totalDurationSeconds = computed(() => {
	let total = 0
	if (props.videoFiles) {
		props.videoFiles.forEach(v => {
			if (v.enabled && v.mediaInfo && v.mediaInfo.duration) total += v.mediaInfo.duration
		})
	}
	return total
})

const nowMs = ref(Date.now())
let nowTickTimer = null
onMounted(() => {
	nowTickTimer = setInterval(() => { nowMs.value = Date.now() }, 1000)
})
onBeforeUnmount(() => {
	if (nowTickTimer) clearInterval(nowTickTimer)
})

const formatCurrentTime = () => {
	const cfg = savedSchedulerConfig.value
	if (cfg && cfg.mode === 'loop') return '--'
	if (cfg && cfg.mode === 'datetime' && cfg.datetime) {
		const elapsedMs = nowMs.value - cfg.datetime
		if (elapsedMs < 0) return '--'
		const elapsed = Math.floor(elapsedMs / 1000)
		const total = totalDurationSeconds.value
		if (total <= 0) return formatDurationHMS(elapsed)
		if (elapsed >= total) return formatDurationHMS(total)
		return formatDurationHMS(elapsed)
	}
	const total = totalDurationSeconds.value
	if (total <= 0) return formatDurationHMS(elapsedSeconds.value)
	return formatDurationHMS(elapsedSeconds.value % total)
}

// Live "now streaming" view helpers
const liveModeLabel = computed(() => (savedSchedulerConfig.value?.mode === 'loop' ? 'Looping' : 'Scheduled'))
const elapsedNumericSeconds = computed(() => {
	const cfg = savedSchedulerConfig.value
	const total = totalDurationSeconds.value
	if (cfg && cfg.mode === 'datetime' && cfg.datetime) {
		const e = Math.floor((nowMs.value - cfg.datetime) / 1000)
		if (e < 0) return 0
		return total > 0 ? Math.min(e, total) : e
	}
	// loop / no datetime → position within the current loop cycle
	return total > 0 ? elapsedSeconds.value % total : elapsedSeconds.value
})
const livePct = computed(() => {
	const total = totalDurationSeconds.value
	if (total <= 0) return 0
	return Math.max(0, Math.min(100, (elapsedNumericSeconds.value / total) * 100))
})

const setScheduleMode = (value) => {
	scheduleMode.value = value
}

// Clears the saved schedule on the server (`mode: null` is its "not configured" state).
const resetSchedulerConfig = async () => {
	if (schedulerConfigProcessing.value) return
	schedulerConfigProcessing.value = true
	try {
		await StreamService.resetStreamScheduleSettings(props.stream._id)
		savedSchedulerConfig.value = { mode: null }
		scheduleMode.value = ScheduleModes.value.Datetime
		scheduledDateTime.value = null
		emit('scheduler-settings-updated', savedSchedulerConfig.value)
		emitAppEvent(APP_EVENTS.SCHEDULE_MODE, null)
	} catch (e) {
		notify({
			group: 'error',
			text: 'Couldn’t reset the schedule.'
		})
	}
	schedulerConfigProcessing.value = false
}

const hasSavedSchedule = computed(() => {
	const cfg = savedSchedulerConfig.value
	return !!cfg && (cfg.mode === 'loop' || (cfg.mode === 'datetime' && !!cfg.datetime))
})

</script>

<template>
	<div class="!p-4 bg-surface-2 rounded-xl border border-surface-3 my-6 md:my-0">
		<header class="flex items-center !mb-2">
			<h3 class="text-base font-medium flex items-center gap-1.5">Stream control <span
					class="text-sm font-normal text-surface-8 inline-flex items-center gap-1.5" v-if="!scheduleConfigLoaded">
					<span class="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"></span> Retrieving
				</span>
			</h3>
			<span v-if="scheduleConfigLoaded && !hasSavedSchedule"
				class="ml-auto inline-flex items-center gap-1.5 text-tiny text-surface-7 bg-surface-3 rounded-full pl-2 pr-2.5 py-0.5">
				<span class="size-1.5 rounded-full bg-surface-6"></span>
				Not configured
			</span>
		</header>
		<p v-if="!streamAlive" class="text-tiny text-surface-7 mt-0.5 mb-3">Choose how your playlist goes live, then start or schedule it.</p>

		<!-- Live: show a "now streaming" status instead of the setup controls -->
		<div v-if="streamAlive">
			<div class="flex items-center gap-2 mb-3">
				<span class="size-2 rounded-full bg-error animate-pulse"></span>
				<span class="text-sm font-semibold text-white">Streaming now</span>
				<span class="ml-auto inline-flex items-center gap-1.5 text-tiny text-surface-7 bg-surface-3 rounded-full px-2 py-0.5">{{ liveModeLabel }}</span>
			</div>
			<!-- Scheduled (plays once): real one-shot progress toward the end -->
			<div v-if="savedSchedulerConfig && savedSchedulerConfig.mode === 'datetime' && totalDurationSeconds > 0">
				<div class="flex justify-between text-tiny text-surface-8 mb-1 tabular-nums">
					<span>{{ formatCurrentTime() }}</span>
					<span>{{ formatTotalDuration() }}</span>
				</div>
				<div class="h-1.5 rounded-full bg-surface-3 overflow-hidden">
					<div class="h-full bg-main rounded-full transition-all duration-1000 ease-linear" :style="{ width: livePct + '%' }"></div>
				</div>
			</div>
			<!-- Loop: continuous playback, no fixed end — show the cycle length instead of a fake bar -->
			<div v-else-if="savedSchedulerConfig && savedSchedulerConfig.mode === 'loop' && totalDurationSeconds > 0"
				class="flex items-center gap-1.5 text-tiny text-surface-8 tabular-nums">
				<span class="inline-flex size-4 items-center justify-center text-surface-7">
					<icon-base width="13" height="13" width-view-box="24" height-view-box="24" fill="none">
						<icon-repeat />
					</icon-base>
				</span>
				Continuous loop · {{ formatTotalDuration() }} per cycle
			</div>
			<p class="text-tiny text-surface-7 mt-3 mb-0">{{ savedSchedulerConfig && savedSchedulerConfig.mode === 'loop' ? 'Your playlist is looping. Use Disable at the top to stop.' : 'Your playlist is playing. Use Disable at the top to stop.' }}</p>
		</div>

		<template v-else>
		<!-- Scheduling depends on the playlist — surface that up front so users don't
		     set a date/time only to discover a video is required at the last step. -->
		<div v-if="!videoFilesLoaded" class="rounded-lg bg-surface-1 border border-surface-3 px-3 py-5">
			<div class="h-4 w-48 mx-auto rounded bg-surface-3 animate-pulse mb-2"></div>
			<div class="h-3 w-64 mx-auto rounded bg-surface-3 animate-pulse"></div>
		</div>
		<div v-else-if="!videoFiles || videoFiles.length === 0"
			class="rounded-lg bg-surface-1 border border-surface-3 px-3 py-5 text-center">
			<p class="text-sm text-surface-9 font-medium mb-0.5">Add a video to get started</p>
			<p class="text-tiny text-surface-6 mb-0">Build your playlist first, then choose how and when it goes live.</p>
		</div>
		<template v-else>
		<Tooltip :active="!stream.enabled" content="Enable the stream first.">
			<TabGroup class="bg-surface-3 w-full gap-x-1 grid grid-cols-2">
				<Tooltip :active="infiniteLoopDisabled" :hide="1000">
					<TabItem :customClasses="classes(scheduleMode === ScheduleModes.Loop)" :disabled="infiniteLoopDisabled"
						:isActive="scheduleMode === ScheduleModes.Loop" label="Loop"
						@click="setScheduleMode(ScheduleModes.Loop)" />
						<template #content>
							<router-link class="underline" to="/subscribe">Upgrade</router-link> to access this feature.
						</template>
				</Tooltip>
				<TabItem :customClasses="classes(scheduleMode === ScheduleModes.Datetime)"
					:disabled="schedulerConfigProcessing || !stream.enabled" :isActive="scheduleMode === ScheduleModes.Datetime"
					label="Date &amp; Time" @click="setScheduleMode(ScheduleModes.Datetime)" />
			</TabGroup>
		</Tooltip>
		<div v-if="scheduleMode === 'datetime'" class="!mt-4">
			<p class="text-sm text-surface-9 font-medium mb-1">Start Date &amp; Time</p>
			<!-- Quick presets: one-click scheduling for common times -->
			<div class="flex flex-wrap gap-2 mb-2">
				<FormButton v-for="preset in schedulePresets" :key="preset.label"
					@click="applySchedulePreset(preset.value)"
					custom-classes="text-xs font-medium px-2.5 h-7 rounded-md bg-surface-3 text-surface-8 hover:bg-surface-4 hover:text-white transition-colors">
					{{ preset.label }}
				</FormButton>
			</div>
			<InlineDateTimePicker v-model="scheduledDateTime" :min-date="new Date(Date.now() + (180 * 1000))" />
			<p class="text-xs text-surface-8 mt-2 mb-0">Your playlist plays once at the scheduled time.</p>
		</div>
		<div v-else-if="scheduleMode === 'loop'" class="!mt-4">
			<p class="text-xs text-surface-8 mb-0">Your playlist plays on a continuous loop, starting as soon as the stream
				goes live.</p>
		</div>
		<Tooltip :active="!videoFiles || videoFiles.length === 0" class="block !mt-4">
			<FormButton type="main" class="w-full flex items-center justify-center mt-2" size="md"
				@click="scheduleMode === 'datetime' ? saveSchedulerConfig() : loopConfirm()"
				:disabled="!canSaveScheduleSetting || !videoFiles || videoFiles.length === 0">
				<span v-if="schedulerConfigProcessing" class="inline-block w-4 h-4 border-2 border-white/60 border-t-transparent rounded-full animate-spin mr-2" />
				{{ scheduleMode === 'datetime' ? 'Schedule Stream' : 'Start Stream' }}
			</FormButton>
			<template #content>
				<p class="text-surface-8 text-sm m-0">Add at least one video before scheduling the stream.</p>
			</template>
		</Tooltip>
		</template>
		<FormButton v-if="videoFilesLoaded && hasSavedSchedule" type="secondary" size="sm"
			class="w-full flex items-center justify-center !mt-2" :disabled="schedulerConfigProcessing"
			@click="showModal('reset-schedule-confirm')">
			Reset schedule
		</FormButton>
		</template>
		<confirm-modal modal-id="feature-upgrade" message="Upgrade your plan to access this feature"
			ok-text="Upgrade Now" cancel-text="Later" @modal-confirm="navigateToPayments()" />
		<confirm-modal modal-size="md" modal-id="infinite-loop-warning"
			message="Infinite Loop mode doesn’t support Facebook" ok-text="Change to Infinite Loop" cancel-text="Cancel"
			body="Facebook destinations connected through the “Log in with Facebook” option can’t be used with Infinite Loop mode due to a Facebook restriction. Facebook destinations added manually with a stream URL and key are not affected."
			@modal-confirm="infiniteLoopConfirmed()" />
		<confirm-modal modal-size="md" modal-id="enable-video-warning"
			message="Enable at least one video to start streaming" :show-confirm="false" cancel-text="Got it"
			body="No videos are turned on for this stream. Enable at least one video from the playlist to start your looped stream." />
		<confirm-modal modal-size="md" modal-id="enable-video-confirm" message="Set live immediately?" :show-confirm="true"
			ok-text="Confirm Infinite Loop" cancel-text="Cancel"
			body="All enabled videos in the playlist will go live now on your destinations and stream as a loop."
			@modal-confirm="saveSchedulerConfig()" />
		<confirm-modal modal-size="sm" modal-id="reset-schedule-confirm" message="Reset the schedule?"
			ok-text="Reset" cancel-text="Keep"
			body="The stream won't go live until you schedule it again or start a loop."
			@modal-confirm="resetSchedulerConfig()" />
		<confirm-modal modal-size="sm" modal-id="stream-status-warning" message="Stream is disabled" ok-text="Enable stream"
			cancel-text="Cancel" body="Please enable the stream before setting your scheduling mode."
			@modal-confirm="saveSchedulerConfig()" />
	</div>
</template>



<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped></style>
